import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useMemo, useState } from "react"
import {
  discardFailedJob,
  getSystemHealth,
  listFailedJobs,
  retryFailedJob,
  type FailedJobHealth,
  type ServiceHealth,
} from "../api/endpoints/system_health"
import {
  AlertCard,
  Button,
  DataTable,
  Drawer,
  EmptyState,
  PageSection,
  StatusPill,
  TableBody,
  TableHead,
  TableRow,
  Td,
  Th,
} from "../components/ui"
import { formatCurrency, formatDateTime, formatEnumLabel } from "../utils/formatting"

function statusTone(status: string) {
  if (status === "operational" || status === "ok" || status === "healthy") return "success" as const
  if (status === "degraded" || status === "delayed" || status === "warning") return "warning" as const
  return "danger" as const
}

function overallBanner(status: string) {
  if (status === "operational") {
    return { variant: "info" as const, label: "All Systems Operational" }
  }

  if (status === "degraded") {
    return { variant: "warning" as const, label: "Operational Issues Detected" }
  }

  return { variant: "danger" as const, label: "Critical Issues Require Attention" }
}

function ServiceCard({
  service,
  onSelect,
}: {
  service: ServiceHealth
  onSelect: () => void
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className="rounded-xl border border-slate-200 bg-white p-4 text-left shadow-sm transition hover:border-slate-300 hover:shadow-md"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-sm font-semibold text-slate-900">{service.name}</div>
          <div className="mt-1 text-xs uppercase tracking-wide text-slate-500">
            {formatEnumLabel(service.category)}
          </div>
        </div>
        <StatusPill tone={statusTone(service.status)}>{formatEnumLabel(service.status)}</StatusPill>
      </div>
      <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
        <div className="rounded-lg bg-slate-50 px-3 py-2">
          <div className="text-[11px] uppercase tracking-wide text-slate-500">Latency</div>
          <div className="mt-1 font-semibold text-slate-900">{service.latencyMs}ms</div>
        </div>
        <div className="rounded-lg bg-slate-50 px-3 py-2">
          <div className="text-[11px] uppercase tracking-wide text-slate-500">Checked</div>
          <div className="mt-1 font-semibold text-slate-900">{formatDateTime(service.lastChecked)}</div>
        </div>
      </div>
    </button>
  )
}

function SummaryTile({
  label,
  value,
  tone = "slate",
}: {
  label: string
  value: string | number
  tone?: "slate" | "emerald" | "amber" | "red"
}) {
  const toneClass =
    tone === "emerald"
      ? "bg-emerald-50 text-emerald-700"
      : tone === "amber"
        ? "bg-amber-50 text-amber-700"
        : tone === "red"
          ? "bg-red-50 text-red-700"
          : "bg-slate-50 text-slate-800"

  return (
    <div className={`rounded-xl border border-slate-200 px-4 py-4 ${toneClass}`}>
      <div className="text-[11px] font-semibold uppercase tracking-wide opacity-75">{label}</div>
      <div className="mt-2 text-2xl font-bold">{value}</div>
    </div>
  )
}

function formatBytes(value: number | null) {
  if (!value || value <= 0) {
    return "-"
  }

  if (value >= 1024 * 1024) {
    return `${(value / (1024 * 1024)).toFixed(1)} MB`
  }

  if (value >= 1024) {
    return `${(value / 1024).toFixed(1)} KB`
  }

  return `${value} B`
}

export function SystemHealthPage() {
  const [selectedServiceName, setSelectedServiceName] = useState<string | null>(null)
  const [selectedFailureId, setSelectedFailureId] = useState<string | null>(null)
  const queryClient = useQueryClient()

  const healthQuery = useQuery({
    queryKey: ["system-health"],
    queryFn: getSystemHealth,
    refetchInterval: 30_000,
  })

  const failedJobsQuery = useQuery({
    queryKey: ["system-health", "failed-jobs"],
    queryFn: listFailedJobs,
    refetchInterval: 30_000,
  })

  const retryMutation = useMutation({
    mutationFn: retryFailedJob,
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["system-health"] }),
        queryClient.invalidateQueries({ queryKey: ["system-health", "failed-jobs"] }),
      ])
    },
  })

  const discardMutation = useMutation({
    mutationFn: discardFailedJob,
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["system-health"] }),
        queryClient.invalidateQueries({ queryKey: ["system-health", "failed-jobs"] }),
      ])
    },
  })

  const report = healthQuery.data?.data
  const services = report?.services ?? []
  const cronJobs = report?.cronJobs ?? []
  const recentFailures = report?.recentFailures ?? []
  const failedJobs = failedJobsQuery.data?.data ?? []
  const selectedService = services.find((service) => service.name === selectedServiceName) ?? null
  const selectedFailure = failedJobs.find((job) => job.failureId === selectedFailureId) ?? null

  const serviceGroups = useMemo(
    () => ({
      critical: services.filter((service) => service.category === "critical"),
      business: services.filter((service) => service.category === "business"),
      infrastructure: services.filter((service) => service.category === "infrastructure"),
    }),
    [services],
  )

  const banner = report ? overallBanner(report.overallStatus) : null

  return (
    <div className="flex flex-col gap-5">
      {banner ? <AlertCard variant={banner.variant} title={banner.label} /> : null}

      <PageSection
        title="System Health"
        actions={
          <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500">
            <span>{report?.checkedAt ? `Last check: ${formatDateTime(report.checkedAt)}` : ""}</span>
            <span>Auto-refresh: 30s</span>
            <Button
              variant="secondary"
              size="sm"
              disabled={healthQuery.isFetching || failedJobsQuery.isFetching}
              onClick={async () => {
                await Promise.all([healthQuery.refetch(), failedJobsQuery.refetch()])
              }}
            >
              {healthQuery.isFetching || failedJobsQuery.isFetching ? "Refreshing..." : "Refresh Now"}
            </Button>
          </div>
        }
      >
        {healthQuery.isLoading ? (
          <p className="p-4 text-sm text-slate-500">Running health checks...</p>
        ) : (
          <div className="space-y-6">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
              <SummaryTile
                label="Open Failed Jobs"
                value={report?.failedJobsSummary.openCount ?? 0}
                tone={report?.failedJobsSummary.openCount ? "red" : "emerald"}
              />
              <SummaryTile
                label="Retryable Jobs"
                value={report?.failedJobsSummary.retryableCount ?? 0}
                tone={report?.failedJobsSummary.retryableCount ? "amber" : "emerald"}
              />
              <SummaryTile
                label="Active Alerts"
                value={report?.activeAlerts.length ?? 0}
                tone={report?.activeAlerts.length ? "amber" : "emerald"}
              />
              <SummaryTile
                label="Backup Status"
                value={formatEnumLabel(report?.backupStatus.status ?? "unknown")}
                tone={
                  report?.backupStatus.status === "critical"
                    ? "red"
                    : report?.backupStatus.status === "warning"
                      ? "amber"
                      : "emerald"
                }
              />
            </div>

            {serviceGroups.critical.length > 0 ? (
              <div>
                <h3 className="mb-3 text-xs font-bold uppercase tracking-wide text-slate-500">
                  Critical Services
                </h3>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
                  {serviceGroups.critical.map((service) => (
                    <ServiceCard
                      key={service.name}
                      service={service}
                      onSelect={() => setSelectedServiceName(service.name)}
                    />
                  ))}
                </div>
              </div>
            ) : null}

            {serviceGroups.business.length > 0 ? (
              <div>
                <h3 className="mb-3 text-xs font-bold uppercase tracking-wide text-slate-500">
                  Business Services
                </h3>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
                  {serviceGroups.business.map((service) => (
                    <ServiceCard
                      key={service.name}
                      service={service}
                      onSelect={() => setSelectedServiceName(service.name)}
                    />
                  ))}
                </div>
              </div>
            ) : null}

            {serviceGroups.infrastructure.length > 0 ? (
              <div>
                <h3 className="mb-3 text-xs font-bold uppercase tracking-wide text-slate-500">
                  Infrastructure
                </h3>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
                  {serviceGroups.infrastructure.map((service) => (
                    <ServiceCard
                      key={service.name}
                      service={service}
                      onSelect={() => setSelectedServiceName(service.name)}
                    />
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        )}
      </PageSection>

      <PageSection title="Backup Status">
        {report ? (
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
              <SummaryTile
                label="Current Status"
                value={formatEnumLabel(report.backupStatus.status)}
                tone={
                  report.backupStatus.status === "critical"
                    ? "red"
                    : report.backupStatus.status === "warning"
                      ? "amber"
                      : "emerald"
                }
              />
              <SummaryTile
                label="Last Success"
                value={report.backupStatus.lastSuccessAt ? formatDateTime(report.backupStatus.lastSuccessAt) : "-"}
              />
              <SummaryTile
                label="Last Failure"
                value={report.backupStatus.lastFailureAt ? formatDateTime(report.backupStatus.lastFailureAt) : "-"}
                tone={report.backupStatus.lastFailureAt ? "amber" : "slate"}
              />
              <SummaryTile
                label="Latest Artifact"
                value={report.backupStatus.lastStorageKey ?? "-"}
              />
            </div>

            {report.backupStatus.history.length === 0 ? (
              <EmptyState title="No Backup History" description="No backup runs have been recorded yet." />
            ) : (
              <div className="overflow-x-auto">
                <DataTable>
                  <TableHead>
                    <Th>Backup Date</Th>
                    <Th>Status</Th>
                    <Th>Attempted</Th>
                    <Th>Completed</Th>
                    <Th>Artifact</Th>
                    <Th>Size</Th>
                    <Th>Error</Th>
                  </TableHead>
                  <TableBody>
                    {report.backupStatus.history.map((row) => (
                      <TableRow key={`${row.backupDate}-${row.attemptedAt}`}>
                        <Td>{row.backupDate}</Td>
                        <Td>
                          <StatusPill tone={statusTone(row.status)}>{formatEnumLabel(row.status)}</StatusPill>
                        </Td>
                        <Td>{formatDateTime(row.attemptedAt)}</Td>
                        <Td>{row.completedAt ? formatDateTime(row.completedAt) : "-"}</Td>
                        <Td>{row.storageKey ?? "-"}</Td>
                        <Td>{formatBytes(row.fileSize)}</Td>
                        <Td>{row.errorMessage ?? "-"}</Td>
                      </TableRow>
                    ))}
                  </TableBody>
                </DataTable>
              </div>
            )}
          </div>
        ) : null}
      </PageSection>

      <PageSection title="Active Alerts">
        {!report || report.activeAlerts.length === 0 ? (
          <EmptyState title="No Active Alerts" description="There are no open system alerts right now." />
        ) : (
          <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
            {report.activeAlerts.map((alert) => (
              <div key={alert.alertId} className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-sm font-semibold text-slate-900">{alert.title}</div>
                    <div className="mt-1 text-xs uppercase tracking-wide text-slate-500">
                      {formatEnumLabel(alert.alertKey)}
                    </div>
                  </div>
                  <StatusPill tone={statusTone(alert.severity)}>{formatEnumLabel(alert.severity)}</StatusPill>
                </div>
                <p className="mt-3 text-sm text-slate-600">{alert.body}</p>
                <div className="mt-3 flex flex-wrap gap-3 text-xs text-slate-500">
                  <span>{formatDateTime(alert.createdAt)}</span>
                  <span>{alert.resourceType ?? "system"}</span>
                  <span>{alert.resourceId ?? "-"}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </PageSection>

      <PageSection title="Background Jobs">
        {cronJobs.length === 0 ? (
          <EmptyState
            title="No Job Data"
            description="Trigger execution telemetry is not available yet."
          />
        ) : (
          <div className="overflow-x-auto">
            <DataTable>
              <TableHead>
                <Th>Job</Th>
                <Th>Schedule</Th>
                <Th>Status</Th>
                <Th>Last Run</Th>
                <Th>Success Rate (24h)</Th>
              </TableHead>
              <TableBody>
                {cronJobs.map((job) => (
                  <TableRow key={job.name}>
                    <Td className="font-medium text-slate-700">{job.name}</Td>
                    <Td>
                      <code className="rounded bg-slate-100 px-2 py-0.5">{job.schedule}</code>
                    </Td>
                    <Td>
                      <StatusPill tone={statusTone(job.status)}>{formatEnumLabel(job.status)}</StatusPill>
                    </Td>
                    <Td>{job.lastRunAt ? formatDateTime(job.lastRunAt) : "-"}</Td>
                    <Td>{job.successRate24h !== null ? `${job.successRate24h}%` : "-"}</Td>
                  </TableRow>
                ))}
              </TableBody>
            </DataTable>
          </div>
        )}
      </PageSection>

      <PageSection title="Failed Jobs">
        {failedJobsQuery.isLoading ? (
          <p className="p-4 text-sm text-slate-500">Loading failed jobs...</p>
        ) : failedJobs.length === 0 ? (
          <EmptyState
            title="No Open Failed Jobs"
            description="The trigger runtime has no unresolved failed jobs at the moment."
          />
        ) : (
          <div className="overflow-x-auto">
            <DataTable>
              <TableHead>
                <Th>Trigger</Th>
                <Th>Entity</Th>
                <Th>Error</Th>
                <Th>Retryable</Th>
                <Th>Created</Th>
                <Th>Retries</Th>
                <Th>Actions</Th>
              </TableHead>
              <TableBody>
                {failedJobs.map((job) => (
                  <TableRow key={job.failureId}>
                    <Td>
                      <button
                        type="button"
                        className="font-semibold text-slate-800 underline decoration-slate-200 underline-offset-4"
                        onClick={() => setSelectedFailureId(job.failureId)}
                      >
                        {formatEnumLabel(job.triggerKey)}
                      </button>
                    </Td>
                    <Td>{job.entityId ?? job.entityType ?? "-"}</Td>
                    <Td>{job.errorCode ?? job.errorMessage}</Td>
                    <Td>
                      <StatusPill tone={job.retryable ? "warning" : "neutral"}>
                        {job.retryable ? "Retryable" : "Manual"}
                      </StatusPill>
                    </Td>
                    <Td>{formatDateTime(job.createdAt)}</Td>
                    <Td>{job.retryCount}</Td>
                    <Td>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          size="sm"
                          variant="secondary"
                          disabled={!job.retryable || retryMutation.isPending}
                          onClick={() => retryMutation.mutate(job.failureId)}
                        >
                          Retry
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={discardMutation.isPending}
                          onClick={() => discardMutation.mutate(job.failureId)}
                        >
                          Discard
                        </Button>
                      </div>
                    </Td>
                  </TableRow>
                ))}
              </TableBody>
            </DataTable>
          </div>
        )}
      </PageSection>

      <PageSection title="Recent Failures (24h)">
        {recentFailures.length === 0 ? (
          <EmptyState
            title="No Recent Failures"
            description="No trigger failures were recorded in the last 24 hours."
          />
        ) : (
          <div className="overflow-x-auto">
            <DataTable>
              <TableHead>
                <Th>Type</Th>
                <Th>Count</Th>
                <Th>Last Occurrence</Th>
              </TableHead>
              <TableBody>
                {recentFailures.map((row) => (
                  <TableRow key={row.type}>
                    <Td>{formatEnumLabel(row.type)}</Td>
                    <Td>
                      <StatusPill tone="danger">{row.count}</StatusPill>
                    </Td>
                    <Td>{row.lastAt ? formatDateTime(row.lastAt) : "-"}</Td>
                  </TableRow>
                ))}
              </TableBody>
            </DataTable>
          </div>
        )}
      </PageSection>

      <Drawer
        title={selectedService?.name ?? "Service Detail"}
        open={Boolean(selectedService)}
        onClose={() => setSelectedServiceName(null)}
      >
        {selectedService ? (
          <div className="space-y-4">
            <PageSection title="Current Status">
              <div className="flex flex-wrap items-center gap-3">
                <StatusPill tone={statusTone(selectedService.status)}>
                  {formatEnumLabel(selectedService.status)}
                </StatusPill>
                <span className="text-sm text-slate-500">
                  Checked {formatDateTime(selectedService.lastChecked)}
                </span>
              </div>
              <div className="mt-3 grid grid-cols-1 gap-3 text-sm text-slate-600 sm:grid-cols-2">
                <p>Category: {formatEnumLabel(selectedService.category)}</p>
                <p>Latency: {selectedService.latencyMs}ms</p>
              </div>
            </PageSection>

            <PageSection title="Metrics">
              {selectedService.metrics && Object.keys(selectedService.metrics).length > 0 ? (
                <div className="grid grid-cols-1 gap-2 text-sm text-slate-600 sm:grid-cols-2">
                  {Object.entries(selectedService.metrics).map(([key, value]) => (
                    <div key={key} className="rounded-md border border-slate-200 px-3 py-2">
                      <div className="text-xs uppercase tracking-wide text-slate-400">
                        {key.replace(/_/g, " ")}
                      </div>
                      <div className="mt-1 font-medium text-slate-800">
                        {typeof value === "number" && key.toLowerCase().includes("amount")
                          ? formatCurrency(value)
                          : String(value ?? "-")}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <EmptyState
                  title="No Metric Detail"
                  description="This health signal does not expose additional metrics."
                />
              )}
            </PageSection>
          </div>
        ) : null}
      </Drawer>

      <Drawer
        title={selectedFailure ? formatEnumLabel(selectedFailure.triggerKey) : "Failed Job Detail"}
        open={Boolean(selectedFailure)}
        onClose={() => setSelectedFailureId(null)}
      >
        {selectedFailure ? (
          <div className="space-y-4">
            <PageSection title="Failure Detail">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="rounded-lg border border-slate-200 px-3 py-2">
                  <div className="text-xs uppercase tracking-wide text-slate-400">Error</div>
                  <div className="mt-1 font-medium text-slate-800">{selectedFailure.errorMessage}</div>
                </div>
                <div className="rounded-lg border border-slate-200 px-3 py-2">
                  <div className="text-xs uppercase tracking-wide text-slate-400">Created</div>
                  <div className="mt-1 font-medium text-slate-800">{formatDateTime(selectedFailure.createdAt)}</div>
                </div>
                <div className="rounded-lg border border-slate-200 px-3 py-2">
                  <div className="text-xs uppercase tracking-wide text-slate-400">Entity</div>
                  <div className="mt-1 font-medium text-slate-800">
                    {selectedFailure.entityType ?? "-"} / {selectedFailure.entityId ?? "-"}
                  </div>
                </div>
                <div className="rounded-lg border border-slate-200 px-3 py-2">
                  <div className="text-xs uppercase tracking-wide text-slate-400">Retries</div>
                  <div className="mt-1 font-medium text-slate-800">{selectedFailure.retryCount}</div>
                </div>
              </div>
            </PageSection>

            <PageSection title="Payload Snapshot">
              <pre className="overflow-x-auto rounded-lg bg-slate-950 p-4 text-xs text-slate-100">
                {JSON.stringify(selectedFailure.sourceMessageJson ?? selectedFailure.payloadJson ?? {}, null, 2)}
              </pre>
            </PageSection>
          </div>
        ) : null}
      </Drawer>
    </div>
  )
}
